import type { UserRole } from '../types'
import { isFieldUserRole, isTechnicalUserRole } from '../config/roles'

export type EtapaOperacao =
  | 'planejamento'
  | 'pre_operacao'
  | 'antes_voo'
  | 'execucao'
  | 'pos_operacao'
  | 'fechamento'

export type TipoObrigacaoOperacional =
  | 'ponto_inicio'
  | 'ponto_fim'
  | 'checklist_tecnico'
  | 'checklist_caar'
  | 'checklist_piloto'
  | 'checklist_aro'
  | 'checklist_epi_sst'
  | 'checklist_logistica'
  | 'checklist_campo'
  | 'checklist_pos_operacao'
  | 'foto_inicio'
  | 'foto_fim'
  | 'gps'
  | 'meteorologia'
  | 'log_voo'
  | 'ocorrencias'

export type SeveridadeObrigacao = 'bloqueante' | 'obrigatoria' | 'recomendada'

export type ParticipanteOperacao = {
  id?: string
  operadora_id: string
  operacao_id: string
  membro_id: string
  user_id?: string | null
  papel: UserRole
  funcao_operacional: string
  horario_inicio_planejado?: string | null
  horario_fim_planejado?: string | null
  manipula_produto?: boolean
  opera_aeronave?: boolean
  responsavel_aro?: boolean
  responsavel_logistica?: boolean
  exige_ponto?: boolean
  ativo?: boolean
}

export type ObrigacaoOperacional = {
  tipo: TipoObrigacaoOperacional
  etapa: EtapaOperacao
  titulo: string
  descricao: string
  severidade: SeveridadeObrigacao
  participanteId?: string
  papel: UserRole
  motivo: string
}

function obrigacao(params: ObrigacaoOperacional): ObrigacaoOperacional {
  return params
}

export function participanteExigePonto(participante: ParticipanteOperacao) {
  return participante.ativo !== false && participante.exige_ponto !== false
}

export function gerarObrigacoesDoParticipante(participante: ParticipanteOperacao): ObrigacaoOperacional[] {
  const obrigacoes: ObrigacaoOperacional[] = []
  const participanteId = participante.id
  const papel = participante.papel

  if (participanteExigePonto(participante)) {
    obrigacoes.push(
      obrigacao({
        tipo: 'ponto_inicio',
        etapa: 'pre_operacao',
        titulo: 'Registrar início da atividade',
        descricao: 'Registrar ponto operacional com data, hora, GPS e evidência quando exigida.',
        severidade: 'bloqueante',
        participanteId,
        papel,
        motivo: 'Todo participante ativo da operação deve registrar início conforme horário planejado.',
      }),
      obrigacao({
        tipo: 'ponto_fim',
        etapa: 'pos_operacao',
        titulo: 'Registrar fim da atividade',
        descricao: 'Registrar encerramento da atividade para fechar jornada operacional e relatório.',
        severidade: 'bloqueante',
        participanteId,
        papel,
        motivo: 'Todo participante ativo da operação deve registrar fim para cálculo de execução e conformidade.',
      }),
    )
  }

  if (papel === 'responsavel_tecnico' || isTechnicalUserRole(papel)) {
    obrigacoes.push(
      obrigacao({
        tipo: 'checklist_tecnico',
        etapa: 'planejamento',
        titulo: 'Checklist técnico da operação',
        descricao: 'Validar produto, recomendação, documentos técnicos, restrições e condições de aplicação.',
        severidade: 'bloqueante',
        participanteId,
        papel,
        motivo: 'Responsável técnico ou consultor deve cumprir apenas obrigações técnicas vinculadas à operação.',
      }),
    )
  }

  if (papel === 'caar') {
    obrigacoes.push(
      obrigacao({
        tipo: 'checklist_caar',
        etapa: 'pre_operacao',
        titulo: 'Checklist CAAR',
        descricao: 'Confirmar condições mínimas da aplicação aeroagrícola remota e habilitação operacional.',
        severidade: 'bloqueante',
        participanteId,
        papel,
        motivo: 'Operações com aplicação aeroagrícola remota exigem controle do aplicador remoto habilitado.',
      }),
    )
  }

  if (papel === 'piloto' || participante.opera_aeronave) {
    obrigacoes.push(
      obrigacao({
        tipo: 'checklist_piloto',
        etapa: 'pre_operacao',
        titulo: 'Checklist do piloto e aeronave',
        descricao: 'Conferir aeronave, bateria, sistema de aplicação, calibração, plano de voo e segurança operacional.',
        severidade: 'bloqueante',
        participanteId,
        papel,
        motivo: 'Quem opera aeronave deve validar condições de voo antes da execução.',
      }),
      obrigacao({
        tipo: 'log_voo',
        etapa: 'pos_operacao',
        titulo: 'Anexar log ou evidência de voo',
        descricao: 'Anexar evidência operacional para rastreabilidade e defesa documental.',
        severidade: 'obrigatoria',
        participanteId,
        papel,
        motivo: 'Logs e evidências fortalecem rastreabilidade da aplicação.',
      }),
    )
  }

  if (participante.responsavel_aro || papel === 'caar' || papel === 'piloto') {
    obrigacoes.push(
      obrigacao({
        tipo: 'checklist_aro',
        etapa: 'antes_voo',
        titulo: 'ARO antes do voo',
        descricao: 'Análise de risco operacional antes da decolagem: área, equipe, meteorologia, distância, terceiros, emergência e autorização.',
        severidade: 'bloqueante',
        participanteId,
        papel,
        motivo: 'O voo não deve iniciar sem ARO concluída por CAAR, piloto ou responsável designado.',
      }),
    )
  }

  if (participante.manipula_produto || papel === 'aplicador') {
    obrigacoes.push(
      obrigacao({
        tipo: 'checklist_epi_sst',
        etapa: 'pre_operacao',
        titulo: 'Checklist EPI/SST',
        descricao: 'Conferir EPI, orientação de uso, exposição, preparo, abastecimento, higiene e descontaminação.',
        severidade: 'bloqueante',
        participanteId,
        papel,
        motivo: 'Quem manipula ou se expõe a produto deve comprovar controle de saúde e segurança.',
      }),
      obrigacao({
        tipo: 'checklist_pos_operacao',
        etapa: 'pos_operacao',
        titulo: 'Descontaminação e pós-exposição',
        descricao: 'Registrar descontaminação, destinação de resíduos e eventuais contatos, respingos ou sintomas.',
        severidade: 'obrigatoria',
        participanteId,
        papel,
        motivo: 'A conformidade não termina no voo; inclui descontaminação e controle pós-atividade.',
      }),
    )
  }

  if (participante.responsavel_logistica || papel === 'motorista') {
    obrigacoes.push(
      obrigacao({
        tipo: 'checklist_logistica',
        etapa: 'pre_operacao',
        titulo: 'Checklist de transporte e logística',
        descricao: 'Conferir veículo, carga, acondicionamento, rota, chegada e ocorrências de transporte.',
        severidade: 'obrigatoria',
        participanteId,
        papel,
        motivo: 'Transporte de equipe, equipamentos ou produto integra a rastreabilidade da operação.',
      }),
    )
  }

  if (isFieldUserRole(papel)) {
    obrigacoes.push(
      obrigacao({
        tipo: 'gps',
        etapa: 'pre_operacao',
        titulo: 'Confirmar localização em campo',
        descricao: 'Coletar GPS para confirmar presença no local da operação.',
        severidade: 'bloqueante',
        participanteId,
        papel,
        motivo: 'Participantes de campo precisam gerar evidência de presença no local operacional.',
      }),
      obrigacao({
        tipo: 'ocorrencias',
        etapa: 'execucao',
        titulo: 'Registrar ocorrências',
        descricao: 'Registrar deriva, falha, chuva, vento, presença de terceiros, animais ou qualquer anormalidade.',
        severidade: 'recomendada',
        participanteId,
        papel,
        motivo: 'Ocorrências alimentam relatório e auditoria da operação.',
      }),
    )
  }

  return obrigacoes
}

export function gerarObrigacoesDosParticipantes(participantes: ParticipanteOperacao[]) {
  return participantes.flatMap(gerarObrigacoesDoParticipante)
}
